import { DispatchCard } from 'src/entities/dispatch';

import { VehicleRowGroup } from './adapter';
import { rotationRowKey } from './courseNumberRows';
import { isMovedOutCard, isTodayDeliveryCard } from './movedOutCards';

type Row = VehicleRowGroup['rows'][number];

/**
 * 前回付けた結果。入力の行・グループ（ラベルを付ける前）から、付けたあとのものを引く。
 * Why not 付ける表記が同じなら入力をそのまま返すだけ: 入力はラベルの無い行なので、薄いカードのある行は毎回作り直され、
 * 無関係な D&D でもその行・グループが描き直される
 */
export type MovedOutLabelCache = {
  rows: WeakMap<Row, Row>;
  groups: WeakMap<VehicleRowGroup, VehicleRowGroup>;
};

export const createMovedOutLabelCache = (): MovedOutLabelCache => ({ rows: new WeakMap(), groups: new WeakMap() });

const sameLabels = (a: Row, b: Row): boolean => a.cards.length === b.cards.length
  && a.cards.every((card, i) => {
    const other = b.cards[i];
    return card.type !== 'delivery' || other.type !== 'delivery' || card.movedOutLabel === other.movedOutLabel;
  });

/**
 * ほかの行へ移した店舗のカード（movedOutCards）に、案件が今どこにあるかを付ける（PM フィードバック 2026-10-27）。
 * 「移動先：6-2」= 行の見出しの番号（勤務一覧の rotations[].courseNumber）と、その行の今日の配車で何番目か。
 * 同じ番号の行が複数あれば（1 便と 2 便が同じコード）「146(2回転目)-5」。見出しの番号が無い行は車両名（回転が複数なら「2号車 2回転目」）。
 * どの行にも無ければ「未割当」。
 *
 * D&D の上書きを当てたあとの全行（絞り込み前）から引くので、D&D の直後も今の場所が出る。
 * 付ける表記が変わらない行・グループは元のオブジェクト（cache があれば前回付けたもの）を返す（applyOverrideRowCards と同じ規約）。
 */
export function applyMovedOutLabels(
  rowGroups: VehicleRowGroup[],
  courseNumberByRowKey: ReadonlyMap<string, string>,
  cache?: MovedOutLabelCache,
): VehicleRowGroup[] {
  const hasMovedOut = rowGroups.some((g) => g.rows.some((r) => r.cards.some(isMovedOutCard)));
  if (!hasMovedOut) return rowGroups;

  const courseNumberOf = (row: Row) => row.source
    && courseNumberByRowKey.get(rotationRowKey(row.source.deliveryId, row.source.serverCycle ?? row.rotationNumber));
  // 1 便と 2 便が同じコードのとき、同じトラックの 2 つの回転が同じ番号になるので、そのときは回転も付ける
  const rowCountByCourseNumber = new Map<string, number>();
  rowGroups.forEach((g) => g.rows.forEach((row) => {
    const number = courseNumberOf(row);
    if (number) rowCountByCourseNumber.set(number, (rowCountByCourseNumber.get(number) ?? 0) + 1);
  }));

  // 案件 → 「6-2」
  const placeByLegRunId = new Map<number, string>();
  for (const group of rowGroups) {
    for (const row of group.rows) {
      const number = courseNumberOf(row);
      const rowLabel = number
        ? ((rowCountByCourseNumber.get(number) ?? 0) > 1 ? `${number}(${row.rotationNumber}回転目)` : number)
        : (row.totalRotations > 1 ? `${group.vehicle.truckName} ${row.rotationNumber}回転目` : group.vehicle.truckName);
      row.cards.filter(isTodayDeliveryCard).forEach((card, index) => {
        (card.legRunIds ?? []).forEach((id) => {
          if (!placeByLegRunId.has(id)) placeByLegRunId.set(id, `${rowLabel}-${index + 1}`);
        });
      });
    }
  }

  const labelOf = (legRunIds: readonly number[]): string => {
    const places = [...new Set(legRunIds.map((id) => placeByLegRunId.get(id) ?? '未割当'))];
    return `移動先：${places.join('・')}`;
  };

  let changed = false;
  const next = rowGroups.map((group) => {
    let rowsChanged = false;
    const rows = group.rows.map((row) => {
      let cardsChanged = false;
      const cards = row.cards.map((card): DispatchCard => {
        if (!isMovedOutCard(card)) return card;
        const movedOutLabel = labelOf(card.movedOutLegRunIds ?? []);
        if (card.movedOutLabel === movedOutLabel) return card;
        cardsChanged = true;
        return { ...card, movedOutLabel };
      });
      if (!cardsChanged) return row;
      rowsChanged = true;
      const labeled = { ...row, cards };
      const previous = cache?.rows.get(row);
      if (previous && sameLabels(previous, labeled)) return previous;
      cache?.rows.set(row, labeled);
      return labeled;
    });
    if (!rowsChanged) return group;
    changed = true;
    const previous = cache?.groups.get(group);
    if (previous && previous.rows.length === rows.length && previous.rows.every((r, i) => r === rows[i])) return previous;
    const labeled = { ...group, rows, vehicle: { ...group.vehicle, cards: rows.flatMap((r) => r.cards) } };
    cache?.groups.set(group, labeled);
    return labeled;
  });
  return changed ? next : rowGroups;
}
